import { useCallback, useContext, useMemo } from "react";
import {
	getArticleTotalPrice as getArticleTotalPriceValue,
	getArticleUnitPrice as getArticleUnitPriceValue,
	getCartTotalPrice as getCartTotalPriceValue,
} from "./cart-pricing";
import { usePriceFormatter } from "./use-price-formatter";
import { useUiData } from "./use-ui-data";
import { ApiDataContext } from "src/api";
import type { Article } from "src/api";

export const useCartPricing = () => {
	const { articles, vat } = useContext(ApiDataContext);
	const {
		cart: { vatEnabled },
		localizer,
	} = useUiData();
	const formatPrice = usePriceFormatter();

	const getArticleUnitPrice = useCallback(
		(article: Article) => getArticleUnitPriceValue(article, vatEnabled),
		[vatEnabled],
	);

	const getArticleTotalPrice = useCallback(
		(article: Article) => getArticleTotalPriceValue(article, vatEnabled),
		[vatEnabled],
	);

	const formatArticleTotalPrice = useCallback(
		(article: Article) => formatPrice(getArticleTotalPrice(article)),
		[formatPrice, getArticleTotalPrice],
	);

	const totalPrice = useMemo(
		() => getCartTotalPriceValue(articles, vatEnabled),
		[articles, vatEnabled],
	);

	const formattedTotalPrice = useMemo(
		() => formatPrice(totalPrice),
		[formatPrice, totalPrice],
	);

	const description = localizer.localizeTerm(
		vatEnabled
			? "cartSummaryDescriptionIncVat"
			: "cartSummaryDescriptionExclVat",
	);
	const priceSummaryDescription =
		vatEnabled && vat ? `${description} (${vat})` : description;

	return {
		formattedTotalPrice,
		formatArticleTotalPrice,
		getArticleUnitPrice,
		getArticleTotalPrice,
		priceSummaryDescription,
		totalPrice,
	};
};
